import { InsightFilter } from '@toggle/toggle';
import { isToday } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { InsightDetails } from '~/components/insights-group/insight-card/insights-details/InsightDetails';
import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { getExploreDirection } from '~/views/dashboard/whats-new-widget/components/insight-card/utils';
import { HORIZONTAL_TABLE_COMBINE_SIZE } from '~/views/explore/components/truncate-combined-icons/TruncateCombinedIcons';
import {
  TABLE_SINGLE_HEIGHT,
  TABLE_SINGLE_WIDTH,
} from '~/views/explore/components/truncate-single-icons/TruncateSingleIcons';
import { getConditionIcons } from '~/views/insights/components/insight-icons/utils';
import { InsightIconsRoot } from '~/views/insights/components/insight-icons-root/InsightIconsRoot';

import styles from './ClickableScenarioInsightTooltip.module.scss';

interface ClickableScenarioInsightTooltipProps {
  insights: InsightFilter[];
  entityTag: string;
  source: PosthogTrackingSource;
}

export const ClickableScenarioInsightTooltip = ({
  insights,
  entityTag,
  source,
}: ClickableScenarioInsightTooltipProps) => {
  const { t } = useTranslation(['insights']);
  const { openDrawer } = useInsightDrawer();

  return (
    <div className={styles.scrollableContainer}>
      {insights.map(insight => {
        const {
          median_return: median,
          horizon,
          stars,
          conditions: insightConditions,
        } = insight.metadata;
        const episodeLabel = t('insights:insightCard.episode', {
          count: insight.metadata.episode_count,
        });
        const isCombined = insight.metadata.kind === 'combination';
        const isStarred = isToday(new Date(insight.created_at));
        const direction = getExploreDirection(insight.metadata.direction);
        const customSize = isCombined
          ? HORIZONTAL_TABLE_COMBINE_SIZE
          : {
              width: `${TABLE_SINGLE_WIDTH}px`,
              height: `${TABLE_SINGLE_HEIGHT}px`,
            };
        const conditions = getConditionIcons({
          kind: insight.metadata.kind,
          conditions: insightConditions,
        });

        const handleClick = () => {
          openDrawer({
            id: insight.id,
            entityTag: insight.metadata.entity,
            trackingProperties: { source },
          });
        };

        return (
          <div key={insight.id} onClick={handleClick}>
            <div className={styles.tooltipWrapper}>
              <InsightIconsRoot
                conditions={conditions}
                direction={direction}
                isStarred={isStarred}
                customSize={customSize}
                isCombined={isCombined}
                layout={isCombined ? 'horizontal' : 'vertical'}
              />
              <InsightDetails
                isTooltip
                median={median}
                horizon={horizon}
                name={insight.card.name}
                stars={stars}
                direction={direction}
                isNewInsight={isStarred}
                entityTag={entityTag}
                episodeLabel={episodeLabel}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
